import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, useLanguage } from "@/context/AppProviders";
import { api, API, formatApiError } from "@/lib/api";
import { FadeIn, Overline } from "@/components/Kinetic";
import { Check, FileDown, FileText, Film, Play, ShieldCheck, MailWarning } from "lucide-react";
import { toast } from "sonner";

export default function MemberArea() {
  const { t, lang } = useLanguage();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [viewed, setViewed] = useState([]);
  const [current, setCurrent] = useState(null);
  const [watchData, setWatchData] = useState(null);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (user === false) { navigate("/auth?next=/espace-membre", { replace: true }); return; }
    if (!user) return;
    api.get("/me/courses").then((r) => {
      setCourses(r.data);
      if (r.data.length > 0) setSelectedCourseId((prev) => prev || r.data[0].id);
    }).catch(() => setCourses([]));
    api.get("/me/progress").then((p) => setViewed(p.data.viewed)).catch(() => {});
  }, [user, navigate]);

  const resendVerification = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success(t.verify.wallResent);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResending(false);
    }
  };

  if (!user || courses === null) {
    return <div className="pt-40 pb-40 px-10 text-ink/40">{t.common.loading}</div>;
  }

  if (user.role !== "admin" && user.email_verified === false) {
    return (
      <div className="pt-40 pb-40 min-h-screen" data-testid="verify-wall">
        <div className="max-w-xl mx-auto px-6 text-center">
          <MailWarning size={48} className="text-ember mx-auto" />
          <h1 className="mt-8 font-display font-semibold tracking-tighter text-4xl text-ink">{t.verify.wallTitle}</h1>
          <p className="mt-4 text-ink/55 text-sm leading-relaxed">{t.verify.wallSub}</p>
          <p className="mt-3 text-xs text-ink/40">{t.verify.wallSpam}</p>
          <button
            onClick={resendVerification}
            disabled={resending}
            data-testid="resend-verification-btn"
            className="btn-glow mt-8 bg-ink hover:bg-ember disabled:opacity-50 text-white px-8 py-4 text-sm tracking-wide transition-colors duration-300"
          >
            {resending ? t.common.loading : t.verify.wallResend}
          </button>
        </div>
      </div>
    );
  }

  if (courses.length === 0) {
    return (
      <div className="pt-40 pb-40 min-h-screen" data-testid="member-no-access">
        <div className="max-w-xl mx-auto px-6 text-center">
          <h1 className="font-display font-semibold tracking-tighter text-4xl text-ink">{t.member.buyFirst}</h1>
          <Link to="/formation" data-testid="member-buy-cta" className="btn-glow mt-8 inline-block bg-ember hover:bg-emberlight text-white px-8 py-4 text-sm tracking-wide transition-colors duration-300">
            {t.member.buyCta}
          </Link>
        </div>
      </div>
    );
  }

  const course = courses.find((c) => c.id === selectedCourseId) || courses[0];
  const allLessons = course.modules.flatMap((m) => m.lessons.map((l) => ({ ...l, module: m })));
  const pdfItems = course.modules
    .filter((m) => m.module_type === "pdf")
    .flatMap((m) => (m.pdfs || []).map((p) => ({ ...p, module: m, isPdf: true })));
  const pdfModuleIds = new Set(course.modules.filter((m) => m.module_type === "pdf").map((m) => m.id));
  const total = allLessons.length + pdfItems.filter((p) => p.has_file).length;
  const viewedCount = allLessons.filter((l) => viewed.includes(l.id)).length + pdfItems.filter((p) => p.has_file && viewed.includes(p.id)).length;
  const pct = total ? Math.round((viewedCount / total) * 100) : 0;
  const pdfs = course.pdfs || [];
  const loosePdfs = pdfs.filter((p) => !pdfModuleIds.has(p.module_id));

  const markViewed = async (lessonId) => {
    if (viewed.includes(lessonId)) return;
    setViewed((v) => [...v, lessonId]);
    try { await api.post("/me/progress", { lesson_id: lessonId }); } catch (e) { /* noop */ }
  };

  const playLesson = async (lesson) => {
    setCurrent(lesson.id);
    setWatchData(null);
    if (!lesson.has_video) return;
    try {
      const { data } = await api.get(`/lessons/${lesson.id}/watch`);
      setWatchData({ lessonId: lesson.id, ...data });
      markViewed(lesson.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openPdf = (pdf) => {
    setCurrent(pdf.id);
    setWatchData(null);
    if (pdf.has_file) markViewed(pdf.id);
  };

  const currentLesson = current ? allLessons.find((l) => l.id === current) : null;
  const currentPdf = current ? pdfItems.find((p) => p.id === current) : null;

  return (
    <div className="pt-28 md:pt-32 pb-28 min-h-screen" data-testid="member-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <FadeIn><Overline>{t.member.hello} {user.name}</Overline></FadeIn>
            <FadeIn delay={0.1}>
              <h1 className="mt-4 font-display font-semibold tracking-tighter text-4xl md:text-6xl text-ink">{t.member.title}</h1>
            </FadeIn>
          </div>
          <FadeIn delay={0.15} className="w-full md:w-80">
            <div className="flex justify-between text-xs uppercase tracking-[0.2em] text-ink/50 mb-2">
              <span>{viewedCount}/{total} {t.member.viewedSuffix}</span>
              <span data-testid="progress-percent">{pct}%</span>
            </div>
            <div className="h-1.5 bg-ink/10 w-full">
              <div className="h-full bg-ember transition-[width] duration-700" style={{ width: `${pct}%` }} data-testid="progress-bar" />
            </div>
          </FadeIn>
        </div>

        {courses.length > 1 && (
          <div className="mt-8 flex flex-wrap gap-2" data-testid="course-selector">
            {courses.map((c) => (
              <button
                key={c.id}
                data-testid={`course-tab-${c.id}`}
                onClick={() => { setSelectedCourseId(c.id); setCurrent(null); setWatchData(null); }}
                className={`text-xs uppercase tracking-[0.2em] px-4 py-2.5 border transition-colors ${course.id === c.id ? "border-ember text-ember" : "border-ink/15 text-ink/50 hover:text-ink"}`}
              >
                {lang === "fr" ? c.title_fr : c.title_en}
              </button>
            ))}
          </div>
        )}

        <div className="mt-14 grid lg:grid-cols-12 gap-10">
          {/* PLAYER */}
          <div className="lg:col-span-8">
            <FadeIn>
              {currentLesson ? (
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-ember">{t.member.module} {currentLesson.module.num}</p>
                  <h2 className="mt-2 font-display font-medium text-2xl text-ink tracking-tight" data-testid="current-lesson-title">
                    {lang === "fr" ? currentLesson.title_fr : currentLesson.title_en}
                  </h2>
                  <div className="mt-6">
                    {currentLesson.has_video ? (
                      watchData && watchData.lessonId === currentLesson.id ? (
                        <div className="relative aspect-video bg-ink overflow-hidden" data-testid="video-player-container">
                          {watchData.provider === "youtube" || watchData.provider === "cloudflare" ? (
                            <iframe
                              data-testid="lesson-youtube-player"
                              src={watchData.embed_url || watchData.iframe_url}
                              title={lang === "fr" ? currentLesson.title_fr : currentLesson.title_en}
                              className="w-full h-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          ) : (
                            <video
                              key={currentLesson.id}
                              data-testid="lesson-video-player"
                              src={`${API}/lessons/${currentLesson.id}/stream`}
                              controls
                              controlsList="nodownload"
                              onContextMenu={(e) => e.preventDefault()}
                              className="w-full h-full"
                            />
                          )}
                          {/* Filigrane anti-partage : email du client en motif diagonal */}
                          <div className="absolute inset-0 z-10 pointer-events-none select-none overflow-hidden" data-testid="client-watermark" aria-hidden="true">
                            <div className="absolute -inset-1/2 flex flex-wrap content-center justify-center gap-x-16 gap-y-10 rotate-[-18deg]">
                              {Array.from({ length: 24 }).map((_, i) => (
                                <span key={i} className="text-white/[0.07] text-sm md:text-base font-body whitespace-nowrap">{user.email}</span>
                              ))}
                            </div>
                            <span className="absolute bottom-2 left-3 text-white/25 text-xs font-body">{user.email}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="aspect-video bg-panel border border-ink/10 flex items-center justify-center" data-testid="video-loading">
                          <p className="text-ink/40 text-sm">{t.common.loading}</p>
                        </div>
                      )
                    ) : (
                      <div className="aspect-video bg-panel border border-dashed border-ink/20 flex flex-col items-center justify-center gap-4" data-testid="video-coming-soon">
                        <Film size={40} className="text-ink/20" />
                        <p className="text-ink/45 text-sm">{t.member.noVideo}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : currentPdf ? (
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-ember">{t.member.module} {currentPdf.module.num}</p>
                  <h2 className="mt-2 font-display font-medium text-2xl text-ink tracking-tight" data-testid="current-lesson-title">{currentPdf.name}</h2>
                  <div className="mt-6 border border-ink/15 bg-surface px-6 py-12 flex flex-col items-center gap-5" data-testid="pdf-download-card">
                    <FileText size={40} className={currentPdf.has_file ? "text-ember" : "text-ink/20"} />
                    <p className="text-ink/55 text-sm text-center max-w-md">{currentPdf.has_file ? t.member.pdfCardSub : t.member.noSheet}</p>
                    {currentPdf.has_file && (
                      <a
                        href={`${API}/pdfs/${currentPdf.id}/download`}
                        data-testid="pdf-download-main"
                        className="btn-glow bg-ink hover:bg-ember text-white px-8 py-4 text-sm tracking-wide transition-colors duration-300 inline-flex items-center gap-2"
                      >
                        <FileDown size={16} /> {t.member.downloadSheet}
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="aspect-video bg-panel border border-dashed border-ink/20 flex flex-col items-center justify-center gap-4" data-testid="select-lesson-placeholder">
                  <Play size={40} className="text-ink/20" />
                  <p className="text-ink/45 text-sm">{t.member.selectLesson}</p>
                </div>
              )}
            </FadeIn>
            {!currentPdf && (
              <FadeIn delay={0.1}>
                <p className="mt-5 flex items-center gap-2 text-xs text-ink/40"><ShieldCheck size={14} className="text-ember" /> {t.member.protected}</p>
              </FadeIn>
            )}

            {loosePdfs.length > 0 && (
              <FadeIn delay={0.15} className="mt-14 border-t border-ink/10 pt-10" data-testid="pdf-section">
                <h3 className="font-display font-medium text-xl text-ink tracking-tight">{t.member.pdfTitle}</h3>
                <p className="mt-2 text-sm text-ink/50">{t.member.pdfSub}</p>
                <div className="mt-6 space-y-3">
                  {loosePdfs.map((p) => (
                    <a
                      key={p.id}
                      href={`${API}/pdfs/${p.id}/download`}
                      data-testid={`pdf-download-${p.id}`}
                      className="flex items-center gap-4 border border-ink/15 hover:border-ember bg-surface px-5 py-4 transition-colors group"
                    >
                      <FileDown size={18} className="text-ember shrink-0" />
                      <span className="flex-1 text-sm text-ink group-hover:text-ember transition-colors">{p.name}</span>
                      <span className="text-xs uppercase tracking-[0.2em] text-ink/40">{t.member.download}</span>
                    </a>
                  ))}
                </div>
              </FadeIn>
            )}
          </div>

          {/* SIDEBAR MODULES */}
          <div className="lg:col-span-4">
            <div className="space-y-6" data-testid="modules-sidebar">
              {course.modules.map((m) => {
                const isPdfModule = m.module_type === "pdf";
                const mTotal = isPdfModule ? (m.pdfs || []).filter((p) => p.has_file).length : m.lessons.length;
                const mViewed = isPdfModule
                  ? (m.pdfs || []).filter((p) => p.has_file && viewed.includes(p.id)).length
                  : m.lessons.filter((l) => viewed.includes(l.id)).length;
                return (
                  <FadeIn key={m.id} className="border border-ink/10 bg-surface">
                    <div className="px-5 py-4 border-b border-ink/10 flex items-center justify-between gap-3">
                      <div className="flex items-baseline gap-3">
                        <span className="font-serif italic text-2xl text-ember/80">{m.num}</span>
                        <h3 className="font-display font-medium text-ink text-sm tracking-tight">{lang === "fr" ? m.title_fr : m.title_en}</h3>
                      </div>
                      <span className="text-[11px] text-ink/40 shrink-0">{mViewed}/{mTotal}</span>
                    </div>
                    <ul>
                      {isPdfModule
                        ? (m.pdfs || []).map((p) => {
                            const isViewed = viewed.includes(p.id);
                            const isCurrent = current === p.id;
                            return (
                              <li key={p.id}>
                                <button
                                  data-testid={`pdf-btn-${p.id}`}
                                  onClick={() => openPdf(p)}
                                  className={`w-full text-left px-5 py-3.5 flex items-center gap-3 text-sm transition-colors ${isCurrent ? "bg-ember/10 text-ink" : "text-ink/60 hover:bg-panel hover:text-ink"}`}
                                >
                                  <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${isViewed ? "bg-ember border-ember" : "border-ink/20"}`}>
                                    {isViewed && <Check size={11} className="text-white" />}
                                  </span>
                                  <span className="flex-1 leading-snug">{p.name}</span>
                                  {p.has_file
                                    ? <FileDown size={12} className={isCurrent ? "text-ember" : "text-ink/25"} />
                                    : <FileText size={12} className="text-ink/20" />}
                                </button>
                              </li>
                            );
                          })
                        : m.lessons.map((l) => {
                        const isViewed = viewed.includes(l.id);
                        const isCurrent = current === l.id;
                        return (
                          <li key={l.id}>
                            <button
                              data-testid={`lesson-btn-${l.id}`}
                              onClick={() => playLesson(l)}
                              className={`w-full text-left px-5 py-3.5 flex items-center gap-3 text-sm transition-colors ${isCurrent ? "bg-ember/10 text-ink" : "text-ink/60 hover:bg-panel hover:text-ink"}`}
                            >
                              <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${isViewed ? "bg-ember border-ember" : "border-ink/20"}`}>
                                {isViewed && <Check size={11} className="text-white" />}
                              </span>
                              <span className="flex-1 leading-snug">{lang === "fr" ? l.title_fr : l.title_en}</span>
                              {l.has_video && <Play size={12} className={isCurrent ? "text-ember" : "text-ink/25"} />}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
